import {Injectable, signal} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {LocalAuthService} from "./localAuth.service";
import {ToastrService} from "ngx-toastr";
import {HttpErrorResponse} from "@angular/common/http";
import {MatDialog} from "@angular/material/dialog";
import {MFAConfirmationDialogComponent} from "../helpers/mfa-confirmation-dialog/mfa-confirmation-dialog.component";
import {LocalStorageService} from "./LocalStorageService";
import {UserModel} from "../models/User-model";


@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private _authenticated = false;
    private _currentUser: UserModel | null = null;

    public isMFAModalOpened = signal<boolean>(false);

    constructor(private router: Router, private localAuthService: LocalAuthService, private localStorageService: LocalStorageService, private toastrService: ToastrService, private dialog: MatDialog, private route: ActivatedRoute) {
    }

    get authenticated(): boolean {
        return this._authenticated;
    }

    get currentUser(): UserModel | null {
        return this._currentUser;
    }

    setAuthenticated(user: UserModel) {
        this._currentUser = user;
        this._authenticated = true;
    }

    clearAuthentication() {
        this._currentUser = null;
        this._authenticated = false;
    }

    login(email: string, password: string) {
        this.localAuthService.loginStart(email, password).subscribe(response => {
                if (response.status === 200) {
                    this.onLoginSuccess();
                }
            }, (error: HttpErrorResponse) => {
                this.handleErrorFromResponse(error);
            }
        )
    }

    onLoginSuccess() {
        this.localAuthService.fetchCurrentUser().subscribe({
            next: (user) => {
                this.setAuthenticated(user);
                this.toastrService.success('User is successfully logged in!', 'You are logged in!', {positionClass: 'toast-top-right'});
                this.router.navigate(['/app']);
                if (!user.mfaEnabled) {
                    this.showMFADialog();
                }
            },
            error: () => {
                this.clearAuthentication();
                this.router.navigate(['/login']);
            }
        });
    }

    logout(): boolean {
        this.clearAuthentication();
        localStorage.removeItem('pureOriginalsFilter');
        localStorage.removeItem('fileServiceFilter');
        localStorage.removeItem('masterFilter');
        this.localStorageService.clearAllFilterKeys();
        this.localAuthService.logoutServer().subscribe();
        this.router.navigate(['/login']);
        return false;
    }

    // The access cookie may still be valid after a development reload. Fetching
    // the user first avoids rotating the refresh token on every rebuild.
    // AuthInterceptor refreshes and retries this request if the access token expired.
    initSession(): Promise<boolean> {
        return new Promise((resolve) => {
            this.localAuthService.fetchCurrentUser().subscribe({
                next: (user) => {
                    this.setAuthenticated(user);
                    if (!user.mfaEnabled) {
                        this.showMFADialog();
                    }
                    resolve(true);
                },
                error: () => {
                    this.clearAuthentication();
                    resolve(false);
                }
            });
        });
    }

    expireSession(): void {
        this.clearAuthentication();
        this.router.navigate(['/login']);
    }

    isLoggedIn(): boolean {
        return this._authenticated && this._currentUser != null;
    }

    isSuperAdmin(): boolean {
        return this.hasRole('SUPER_ADMIN');
    }

    isAdmin(): boolean {
        return this.hasRole('ADMIN') || this.isSuperAdmin();
    }

    isUser(): boolean {
        return this.hasRole('USER');
    }

    isModerator(): boolean {
        return this.hasRole('MODERATOR');
    }

    private hasRole(targetRole: string): boolean {
        return this._currentUser?.authorities?.some((role) => role.authority === targetRole) ?? false;
    }

    showMFADialog() {
        setTimeout(() => {
            const dialogRef = this.dialog.open(MFAConfirmationDialogComponent, {
                width: "350px",
                disableClose: true,
                data: "Two-factor authentication is required for all accounts. Please enable MFA to continue using the dashboard."
            })
            dialogRef.afterClosed().subscribe(confirmed => {
                if (confirmed) {
                    this.isMFAModalOpened.set(true);
                } else {
                    this.isMFAModalOpened.set(false);
                    this.logout();
                }
            })
        }, 3000)
    }

    handleErrorFromResponse(error: HttpErrorResponse) {
        if (error.status == 302) {
            console.log(this.route);
            this.router.navigate(["/login/two-factor"], {queryParams: {email: error.error.token}});
        }
        if (error.status == 400) {
            console.log('Error occured', error.status);
            this.toastrService.error('Bad request', 'Error occured!', {positionClass: 'toast-top-right'});
        }
        if (error.status == 401) {
            console.log('Error occured', error.status);
            this.toastrService.error('Try again', 'Incorrect Password', {positionClass: 'toast-top-right'});
        }
        if (error.status == 403) {
            console.log('Error occured', error.status);
            this.toastrService.error('Your IP address is blocked for 2 minutes', 'IP address blocked', {positionClass: 'toast-top-right'});
        }
        if (error.status == 404) {
            console.log('Error occured', error.status);
            this.toastrService.error('User not found', 'No User!', {positionClass: 'toast-top-right'});
        }
        if (error.status == 405) {
            console.log('Error occured', error.status);
            this.toastrService.error('Account is not activated yet!', 'Not Activated!', {positionClass: 'toast-top-right'});
        }
    }


}
